import type { Task } from '../../services/api/tasksService';
import type { DailyDispatch } from '../../services/api/dailyDispatchService';

export interface CalendarDay {
  date: string;
  dayNumber: number;
  inMonth: boolean;
  isToday: boolean;
}

const pad = (value: number) => String(value).padStart(2, '0');

export const toLocalIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const monthBounds = (month: Date): { from: string; to: string } => ({
  from: toLocalIsoDate(new Date(month.getFullYear(), month.getMonth(), 1)),
  to: toLocalIsoDate(new Date(month.getFullYear(), month.getMonth() + 1, 0)),
});

export const buildMonthGrid = (month: Date, today = new Date()): CalendarDay[] => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());
  const todayKey = toLocalIsoDate(today);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    const dateKey = toLocalIsoDate(date);
    return {
      date: dateKey,
      dayNumber: date.getDate(),
      inMonth: date.getMonth() === month.getMonth(),
      isToday: dateKey === todayKey,
    };
  });
};

export const dateInRange = (date: string | null | undefined, from: string, to: string): boolean => {
  const key = date?.slice(0, 10);
  return Boolean(key && key >= from && key <= to);
};

export const localDateKey = (value: string | null | undefined): string | null => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return toLocalIsoDate(date);
};

export const completedTaskDate = (task: Task): string | null => {
  if (task.completionDate) return localDateKey(task.completionDate);
  const status = String(task.lifecycleStatus || task.status || '').toUpperCase();
  return status === 'INSPECTION_PASSED' ? localDateKey(task.updatedAt) : null;
};

export const taskTeamIds = (task: Task): string[] => {
  const ids = new Set<string>();
  if (task.teamId) ids.add(task.teamId);
  task.teamAssignments?.forEach((assignment) => {
    const teamId = assignment.teamId ?? assignment.team_id ?? assignment.team?.id;
    if (teamId) ids.add(teamId);
  });
  return [...ids];
};

export const weatherGuidance = (weather?: string | null, temperatureC?: number | null): string[] => {
  const condition = weather?.trim().toLowerCase() ?? '';
  const guidance: string[] = [];

  if (/rain|shower|storm|thunder|wet/.test(condition)) {
    guidance.push('Check excavation drainage, concrete protection, access routes, and electrical work before release.');
  }
  if (/wind|gust/.test(condition)) {
    guidance.push('Review lifting, scaffolding, roof work, and work-at-height limits before crews proceed.');
  }
  if (/fog|mist|smoke|haze/.test(condition)) {
    guidance.push('Confirm visibility for vehicle movements, lifting signals, and perimeter work.');
  }
  if (typeof temperatureC === 'number' && temperatureC >= 32) {
    guidance.push('Plan hydration, shaded recovery breaks, and heat-stress monitoring.');
  }
  if (typeof temperatureC === 'number' && temperatureC <= 18) {
    guidance.push('Confirm curing, coating, and material temperature requirements.');
  }
  if (!guidance.length) {
    guidance.push('Record observed ground, access, lifting, concrete, and worker-safety impacts before submitting the report.');
  }
  return guidance;
};

export const presetDateRange = (months: number, today = new Date()): { from: string; to: string } => {
  const from = new Date(today.getFullYear(), today.getMonth() - months, today.getDate());
  return { from: toLocalIsoDate(from), to: toLocalIsoDate(today) };
};

export const isDispatchSendable = (
  dispatch?: Pick<DailyDispatch, 'status' | 'manualHold'> | null
): boolean => Boolean(
  dispatch
  && !dispatch.manualHold
  && ['draft', 'failed', 'partially_sent'].includes(dispatch.status)
);

export type InspectionVerdictStatus = 'INSPECTION_PASSED' | 'INSPECTION_FAILED';

export type InspectionVerdict = 'passed' | 'conditional' | 'failed';

export const verdictLifecycleStatus = (verdict: InspectionVerdict): InspectionVerdictStatus =>
  verdict === 'passed' ? 'INSPECTION_PASSED' : 'INSPECTION_FAILED';

export const taskAwaitingInspection = (task: Pick<Task, 'lifecycleStatus' | 'status'>): boolean =>
  ['INSPECTION_PENDING', 'INSPECTION_FAILED'].includes(
    String(task.lifecycleStatus || task.status || '').toUpperCase()
  );

/**
 * Build the defect record payload for a failed inspection from the reasons
 * and analysis the supervisor captured. Safety-related rejections escalate
 * severity so the defect board triages them ahead of workmanship issues.
 */
export const failDefectPayload = (
  task: Pick<Task, 'title'>,
  reasons: string[],
  analysis: string
): { title: string; description?: string; severity: 'medium' | 'high' } => {
  const description = [
    analysis.trim(),
    reasons.length ? `Reasons: ${reasons.join('; ')}` : '',
  ].filter(Boolean).join('\n\n');
  return {
    title: `Failed inspection: ${task.title}`.slice(0, 200),
    description: description || undefined,
    severity: reasons.some((reason) => /safety/i.test(reason)) ? 'high' : 'medium',
  };
};
